import { useApi } from 'api/api-context';
import { useMemo } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH } from 'routes/paths';

interface Props {
  permission?: string;
  oneOfPermissions?: string[];
  allOfPermissions?: string[];
  redirect?: string;
  scope?: 'anywhere' | 'global';
}

/**
 * Requires the user to have the given permissions.
 */
export default function RequirePermission({
  permission,
  oneOfPermissions,
  allOfPermissions,
  redirect = DASHBOARD_PAGE_PATH.original,
  scope = 'anywhere',
}: Props) {
  const { hasPermissionAnywhere, hasPermissionGlobally } = useApi();
  const hasPermission = scope === 'global' ? hasPermissionGlobally : hasPermissionAnywhere;

  const hasAccess = useMemo(() => {
    if (permission && !hasPermission(permission)) {
      return false;
    }

    if (oneOfPermissions && !oneOfPermissions.some(hasPermission)) {
      return false;
    }

    if (allOfPermissions && !allOfPermissions.every(hasPermission)) {
      return false;
    }

    return true;
  }, [allOfPermissions, hasPermission, oneOfPermissions, permission]);

  if (!hasAccess) {
    return <Navigate to={redirect} />;
  }

  return <Outlet />;
}
